import * as React from 'react';
import { observer } from 'mobx-react';
import { classes, style } from 'typestyle';
import { ImageTile } from './image-tile';
import { constantGoldenRatio } from '../theme/constant';
import { ButtonPill } from './button-pill';
import { PageAbout } from './page-about';
import { percent, px } from 'csx';
import { mediaTablet, mediaMobileOnly } from '../theme/media';
import { ImageStack } from './image-stack';
import { centerCenter } from 'csstips';
import {ImagePreloader} from '../types/fn';
import {ResponsiveImageTile} from './responsive-image-tile';
import VideoModal from './video/modal';
import {ImageAspect} from '../constants';
import InlinePreviewImage from './inline-preview/image';
import youtubeVideoEmbed from '../lib/youtubeVideoEmbed';
import {RatioTile} from './ratio-tile';

export interface PageHomeSectionsModel {
  section1ActionLabel: string;
  section1ActionTarget: string;
  section1Content: string;
  section1Title: string;
  section1Image1: string;
  section1Image2: string;
  section1Image3: string;
  section2Title: string;
  section2Subtitle1: string;
  section2Subtitle2: string;
  section2Content1: string;
  section2Content2: string;
  section2Image: string;
  section3Title: string;
  section3Subtitle1: string;
  section3Subtitle2: string;
  section3Content1: string;
  section3Content2: string;
  section3Image: string;
  videoId?: string;
}

export interface PageHomeSectionsProps {
  model: PageHomeSectionsModel;
  preload: ImagePreloader;
  preloadRaw: ImagePreloader;
}

@observer
export class PageHomeSections extends React.Component<PageHomeSectionsProps, any> {
  render() {
    const { model, preload, preloadRaw } = this.props;
    
    return (
      <div className={classes(PageHomeSections.styles.container, PageAbout.styles.sections)}>
        <div className={PageAbout.styles.section}>
          <div>
            <h2>{model.section1Title}</h2>
            <p>{model.section1Content}</p>
            <div className={PageHomeSections.styles.buttons}>
              <ButtonPill
                muted={true}
                href={model.section1ActionTarget}
                children={model.section1ActionLabel}
              />
            </div>
          </div>
          <div>
            {!model.videoId && (
              <ImageStack
                className={PageHomeSections.styles.imageStack}
                top={model.section1Image1}
                middle={model.section1Image2}
                bottom={model.section1Image3}
                preload={preload}
              />
            )}
            {model.videoId && (
              <div className={PageHomeSections.styles.playerContainer}>
                <ResponsiveImageTile
                  preload={preloadRaw}
                  ratio={1.6}
                  className={PageHomeSections.styles.player}
                  identity={`https://img.youtube.com/vi/${model.videoId}/0.jpg`}
                >
                  <div className={PageHomeSections.styles.playerInner}>
                    <VideoModal
                      embed={youtubeVideoEmbed(model.videoId)}
                    />
                  </div>
                </ResponsiveImageTile>
              </div>
            )}
          </div>
        </div>
        <div className={PageAbout.styles.section}>
          <div>
            <ResponsiveImageTile
              className={PageAbout.styles.sectionImage}
              identity={model.section2Image}
              preload={preload}
              ratio={constantGoldenRatio}
            />
          </div>
          <div>
            <h3>{model.section2Title}</h3>
            <h4>{model.section2Subtitle1}</h4>
            <p>{model.section2Content1}</p>
            <h4>{model.section2Subtitle2}</h4>
            <p>{model.section2Content2}</p>
          </div>
        </div>
        <div className={PageAbout.styles.section}>
          <div>
            <h3>{model.section3Title}</h3>
            <h4>{model.section3Subtitle1}</h4>
            <p>{model.section3Content1}</p>
            <h4>{model.section3Subtitle2}</h4>
            <p>{model.section3Content2}</p>
          </div>
          <div>
            <ResponsiveImageTile
              className={PageAbout.styles.sectionImage}
              identity={model.section3Image}
              ratio={constantGoldenRatio}
              preload={preload}
            />
          </div>
        </div>
      </div>
    )
  }
  
  static styles = {
    container: style(
      mediaMobileOnly({
        padding: px(20)
      }),
      mediaTablet({
        padding: px(60)
      }),
    ),
    buttons: style(
      mediaMobileOnly({
        width: percent(100),
        textAlign: 'center',
        padding: '0 40px'
      }),
      mediaTablet({
        width: percent(100),
        padding: '0 40px'
      })
    ),
    imageStack: style(
      mediaMobileOnly({
        width: percent(100),
        margin: '40px 0 0 0'
      }),
      mediaTablet({
        width: percent(75),
        margin: '0 auto'
      })
    ),
    playerContainer: style(
      {
        ...centerCenter,
        width: percent(100),
        margin: '0 auto'
      },
      mediaMobileOnly({
        marginTop: px(40)
      })
    ),
    player: style(
      {
        position: 'relative',
        maxWidth: px(640),
        width: percent(100),
      },
    ),
    playerInner: style({
      ...centerCenter,
      flex: 1,
      width: percent(100),
      height: percent(100),
    }),
    video: style({
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: percent(100),
      height: percent(100)
    })
  }
}